import { z } from 'zod';

// ============================================================
// Kontrak integrasi ciptaimaji.id -> ImajiPRO (v2)
// POST {IMAJIPRO_API_URL}/integrations/v1/orders
// Auth: header X-API-Key (kunci "ipk_..." dari Settings > Integrasi ImajiPRO)
// Idempotency: externalId — kiriman ulang dijawab 200 created=false,
// data tidak diubah, pembayaran baru tetap dicatat.
// Bentuk mengikuti schema generik ImajiPRO /integrations/v1, bukan lagi
// kontrak khusus; ImajiPRO adalah pemilik schema, file ini cerminannya.
// ============================================================

export const IMAJIPRO_CONTRACT_VERSION = 2;

export const imajiproOptionSchema = z.object({
  label: z.string().max(200),
  value: z.string().max(1000),
});

/** File cetak: presigned GET URL bucket privat, berlaku 7 hari.
 *  ImajiPRO mengunduh dan menyimpan sendiri saat verifikasi. */
export const imajiproDesignFileSchema = z.object({
  url: z.string().url().max(2000),
  name: z.string().max(300).optional(),
  page: z.number().int().min(1).max(1000).optional(),
  kind: z.string().max(50).optional(), // mis. print, preview, cut
});

export const externalOrderItemSchema = z.object({
  externalItemId: z.string().max(100).optional(),
  /** Kunci pemetaan produk di ImajiPRO. CI memakai productSlug. */
  productKey: z.string().min(1).max(120),
  productName: z.string().min(1).max(200),
  qty: z.number().int().positive().max(1_000_000),
  unitPrice: z.number().nonnegative(), // IDR bulat
  totalPrice: z.number().nonnegative(),
  options: z.array(imajiproOptionSchema).max(100).default([]),
  designFiles: z.array(imajiproDesignFileSchema).max(100).default([]),
  note: z.string().max(2000).optional(),
});

export const externalPaymentSchema = z.object({
  /** Kunci idempotensi pembayaran di ImajiPRO. CI memakai providerRef. */
  externalRef: z.string().min(1).max(150),
  amount: z.number().positive(),
  method: z.enum(['cash', 'transfer', 'qris', 'other']).default('transfer'),
  paidAt: z.string().datetime().optional(),
  note: z.string().max(500).optional(),
});

export const externalOrderSchema = z.object({
  /** Kunci idempotensi order. CI memakai order.number (CI-...). */
  externalId: z.string().min(1).max(100),
  externalNumber: z.string().max(100).optional(),
  customer: z.object({
    name: z.string().min(1).max(200),
    phone: z.string().max(30).optional(),
    email: z.string().max(200).optional(),
  }),
  shipping: z
    .object({
      recipient: z.string().max(200).optional(),
      phone: z.string().max(30).optional(),
      address: z.string().max(1000).optional(),
      addressNote: z.string().max(500).optional(),
      postalCode: z.string().max(20).optional(),
      destination: z.string().max(300).optional(),
      courier: z.string().max(100).optional(),
      service: z.string().max(100).optional(),
      etd: z.string().max(50).optional(),
      weightGram: z.number().int().min(0).optional(),
      cost: z.number().min(0).default(0),
    })
    .optional(),
  priority: z.enum(['normal', 'vip', 'urgent']).default('normal'),
  deadline: z.string().datetime().optional(),
  discount: z.number().min(0).default(0),
  customerNote: z.string().max(2000).optional(),
  items: z.array(externalOrderItemSchema).min(1).max(100),
  payments: z.array(externalPaymentSchema).max(50).default([]),
});

/** Jawaban POST /integrations/v1/orders: 201 created=true, 200 created=false (duplikat). */
export const externalOrderResponseSchema = z.object({
  status: z.literal('ok'),
  created: z.boolean(),
  order: z.looseObject({
    id: z.string().min(1),
    orderCode: z.string().nullish(),
    overallStatus: z.string(),
    paymentStatus: z.string().optional(),
  }),
});

export type ImajiproOption = z.infer<typeof imajiproOptionSchema>;
export type ImajiproDesignFile = z.infer<typeof imajiproDesignFileSchema>;
export type ExternalOrderItem = z.infer<typeof externalOrderItemSchema>;
export type ExternalPayment = z.infer<typeof externalPaymentSchema>;
export type ExternalOrder = z.infer<typeof externalOrderSchema>;
export type ExternalOrderResponse = z.infer<typeof externalOrderResponseSchema>;

// ============================================================
// Snapshot produksi dari webhook ImajiPRO (kolom Order.imajiproSnapshot).
// Disimpan mentah; dibaca lewat schema ini supaya hanya field yang
// memang untuk pelanggan yang keluar di API (key lain dibuang).
// ============================================================

export const imajiproSnapshotItemSchema = z.object({
  /** id OrderItem CI. */
  externalItemId: z.string().nullish(),
  productName: z.string().nullish(),
  qty: z.number().int().nonnegative(),
  currentStage: z.object({ id: z.string().optional(), name: z.string() }).nullish(),
  finishedAt: z.string().nullish(),
});

export const imajiproSnapshotSchema = z.object({
  orderCode: z.string().nullish(),
  overallStatus: z.string(),
  paymentStatus: z.string().nullish(),
  items: z.array(imajiproSnapshotItemSchema).default([]),
});

export type ImajiproSnapshotItem = z.infer<typeof imajiproSnapshotItemSchema>;
export type ImajiproSnapshot = z.infer<typeof imajiproSnapshotSchema>;
